import { useRef, useState } from "react";
import { Copy, EllipsisVertical, Plus, Trash2, Wine } from "lucide-react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { volumesOptions } from "@/constants/options/volumes.option";
import { ProductDto, ProductUSA } from "@/types/api/api-types";
import { QuotationUpdateBoFormData } from "@/types/forms/bo/expeditionBo.form";
import { ProductDrawer } from "../../productDrawer";
import { ProductProfilePickerDrawer } from "../../productProfilePickerDrawer";
import {
  DeliveryContentProps,
  DrawerState,
} from "@/components/backOffice/expeditionForm/expeditionForm.types";
import { DeliveryContentLineProfileControls } from "./deliveryContentLineProfileControls";
import { DeliveryContentTotal } from "./deliveryContentTotal";
import { getDefaultDeliveryContentRow, getDestinationRegion } from "./deliveryContent.utils";
import clsx from "clsx";
import { useWineColorOptions } from "@/hooks/useEnumOptions";

export const DeliveryContent = ({
  products,
  isUsaProducts,
  companyId,
  countryCode,
  viaImportedInvoice,
}: DeliveryContentProps) => {
  const { t } = useTranslation("boExpeditionForm");
  const wineColorOptions = useWineColorOptions();
  const [drawerState, setDrawerState] = useState<DrawerState>({
    open: false,
    product: null,
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerDefaultColor, setPickerDefaultColor] = useState<string | undefined>();
  const profileLineIndicesRef = useRef<number[] | null>(null);
  const [selectedFieldIds, setSelectedFieldIds] = useState<Set<string>>(new Set());
  const { control, setValue, getValues } = useFormContext<QuotationUpdateBoFormData>();
  const formImporterId = useWatch({ control, name: "importerId" });

  const { fields, append, remove, insert } = useFieldArray({
    control,
    name: "deliveryContent",
  });

  const destinationRegion = getDestinationRegion(countryCode);
  const allFieldsShown = !viaImportedInvoice || destinationRegion === "OTHER";
  const allowAllFields = destinationRegion === "OTHER" || !viaImportedInvoice;
  const defaultValues = getDefaultDeliveryContentRow(destinationRegion, {}, viaImportedInvoice);
  const profileControlsShown = destinationRegion !== "UE";

  const selectedIndices = fields
    .map((field, index) => (selectedFieldIds.has(field.id) ? index : -1))
    .filter((index) => index !== -1);
  const selectedCount = selectedIndices.length;
  const allSelected = fields.length > 0 && selectedCount === fields.length;

  const toggleLineSelection = (fieldId: string, checked: boolean) => {
    setSelectedFieldIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(fieldId);
      else next.delete(fieldId);
      return next;
    });
  };

  const toggleAllSelection = (checked: boolean) => {
    setSelectedFieldIds(checked ? new Set(fields.map((field) => field.id)) : new Set());
  };

  const clearSelection = () => setSelectedFieldIds(new Set());

  const duplicate = (index: number) => {
    const item = getValues(`deliveryContent.${index}`);
    insert(index + 1, item);
  };

  const removeLine = (index: number) => {
    toggleLineSelection(fields[index].id, false);
    remove(index);
  };

  const assignProductToLine = (lineIndex: number, productIdValue: string) => {
    if (isUsaProducts) {
      setValue(`deliveryContent.${lineIndex}.productUSAId`, productIdValue, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue(`deliveryContent.${lineIndex}.productId`, undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else {
      setValue(`deliveryContent.${lineIndex}.productId`, productIdValue, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue(`deliveryContent.${lineIndex}.productUSAId`, undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const assignProductToTargetLines = (productIdValue: string) => {
    const indices = profileLineIndicesRef.current;
    if (!indices) return;
    indices.forEach((idx) => assignProductToLine(idx, productIdValue));
    if (indices.length > 1) clearSelection();
    profileLineIndicesRef.current = null;
  };

  const openPickerForLines = (indices: number[]) => {
    if (indices.length === 0) return;
    profileLineIndicesRef.current = indices;
    const colors = new Set(indices.map((idx) => getValues(`deliveryContent.${idx}.color`)));
    setPickerDefaultColor(colors.size === 1 ? [...colors][0] : undefined);
    setPickerOpen(true);
  };

  const openCreateForLines = (indices: number[]) => {
    if (indices.length === 0) return;
    profileLineIndicesRef.current = indices;
    setDrawerState({ open: true, product: null });
  };

  const openPickerForLine = (index: number) => openPickerForLines([index]);

  const openCreateForLine = (index: number) => openCreateForLines([index]);

  const openEditForLine = (index: number, product: ProductDto | ProductUSA) => {
    profileLineIndicesRef.current = [index];
    setDrawerState({ open: true, product });
  };

  const closePicker = () => {
    setPickerOpen(false);
    profileLineIndicesRef.current = null;
  };

  return (
    <AccordionItem
      value="deliveryContent"
      className="shadow-basic flex w-full flex-col items-start justify-center gap-4 rounded-xl border border-gray-200 bg-slate-100 p-6"
    >
      <AccordionTrigger className="flex w-full flex-row items-start justify-between gap-2 self-start !no-underline">
        <div className="flex flex-row items-center gap-2 self-start">
          <Wine className="h-6 w-6 !rotate-0 text-blue-600" />
          <p className="text-xl font-bold">{t("deliveryContent.title")}</p>
        </div>
      </AccordionTrigger>
      <AccordionContent forceMount className="flex w-full flex-col gap-2">
        {profileControlsShown && fields.length > 1 && (
          <div className="flex w-full flex-row flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
            <label className="flex cursor-pointer flex-row items-center gap-2 text-sm text-gray-800">
              <Checkbox
                checked={allSelected ? true : selectedCount > 0 ? "indeterminate" : false}
                onCheckedChange={(checked) => toggleAllSelection(checked === true)}
              />
              {selectedCount > 0
                ? t("deliveryContentBulk.selectedCount", { count: selectedCount })
                : t("deliveryContentBulk.selectAll")}
            </label>
            {selectedCount > 0 && (
              <div className="ml-auto flex flex-row flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => openPickerForLines(selectedIndices)}
                >
                  {t("deliveryContentBulk.chooseProfile")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => openCreateForLines(selectedIndices)}
                >
                  {t("deliveryContentBulk.createProfile")}
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={clearSelection}>
                  {t("deliveryContentBulk.clearSelection")}
                </Button>
              </div>
            )}
          </div>
        )}
        {fields.map((field, index) => (
          <div
            key={field.id}
            className={clsx(
              "flex flex-col items-start gap-2 rounded-lg bg-slate-200 p-2",
              selectedFieldIds.has(field.id) && "ring-2 ring-blue-400",
            )}
          >
            <div
              className={clsx(
                "w-full items-center gap-2",
                !allFieldsShown && "flex flex-row",
                allFieldsShown &&
                  "grid grid-cols-2 flex-row @xl:grid-cols-3 @2xl:grid-cols-4 @3xl:grid-cols-5 @5xl:flex",
              )}
            >
              {allFieldsShown && (
                <ControlledUnifiedInput
                  control={control}
                  name={`deliveryContent.${index}.wineName`}
                  wrapperClassName={clsx(
                    allFieldsShown && "@5xl:w-full col-span-2",
                    !allFieldsShown && "w-full",
                  )}
                  label={t("deliveryContent.wineNameLabel")}
                  required
                  disabled={!allowAllFields}
                />
              )}
              <ControlledSelectV2
                control={control}
                name={`deliveryContent.${index}.volume`}
                options={volumesOptions.map((volume) => ({
                  label: volume.label,
                  value: volume.value.toString(),
                }))}
                wrapperClassName={clsx(
                  allFieldsShown && "@5xl:w-fit col-span-1",
                  !allFieldsShown && "w-full",
                )}
                label={t("deliveryContent.volumeLabel")}
                required
                triggerClassName="@5xl:w-[100px]"
              />
              {allFieldsShown && (
                <ControlledUnifiedInput
                  control={control}
                  name={`deliveryContent.${index}.alcoholPercent`}
                  className="col-span-1 @5xl:w-[50px]"
                  wrapperClassName={clsx(!allFieldsShown && "w-full")}
                  label={t("deliveryContent.alcoholPercentLabel")}
                  required
                  inputType="float"
                  maxDecimals={2}
                  disabled={!allowAllFields}
                />
              )}
              {(allFieldsShown || destinationRegion === "USA") && (
                <ControlledSelectV2
                  control={control}
                  name={`deliveryContent.${index}.color`}
                  options={wineColorOptions}
                  wrapperClassName={clsx(
                    allFieldsShown && "@5xl:w-fit col-span-1",
                    !allFieldsShown && "w-full",
                  )}
                  label={t("deliveryContent.colorLabel")}
                  required
                  triggerClassName={clsx(
                    allFieldsShown && "@5xl:w-[130px] col-span-1",
                    !allFieldsShown && "w-full",
                  )}
                />
              )}
              {allFieldsShown && (
                <ControlledUnifiedInput
                  control={control}
                  name={`deliveryContent.${index}.year`}
                  wrapperClassName={clsx(!allFieldsShown && "w-full")}
                  label={t("deliveryContent.yearLabel")}
                  className={clsx(allFieldsShown && "w-[80px]", !allFieldsShown && "w-full")}
                  disabled={!allowAllFields}
                />
              )}
              <ControlledUnifiedInput
                control={control}
                name={`deliveryContent.${index}.numberOfBottles`}
                label={t("deliveryContent.quantityLabel")}
                required
                className="w-[80px]"
                inputType="integer"
                wrapperClassName={clsx(!allFieldsShown && "w-full")}
              />
              <ControlledUnifiedInput
                control={control}
                name={`deliveryContent.${index}.unitPrice`}
                label={t("deliveryContent.unitPriceLabel")}
                required
                className="w-[80px]"
                inputType="float"
                maxDecimals={2}
                wrapperClassName={clsx(!allFieldsShown && "w-full")}
              />
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" className="h-fit w-fit px-1 [&_svg]:h-6 [&_svg]:w-6">
                    <EllipsisVertical className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-fit">
                  <Button
                    className="mb-2 flex w-full flex-row items-center gap-2"
                    variant="outline"
                    type="button"
                    onClick={() => duplicate(index)}
                  >
                    <Copy className="h-4 w-4" />
                    {t("deliveryContent.duplicateItem")}
                  </Button>
                  <Button
                    className="flex w-full flex-row items-center gap-2"
                    variant="outline"
                    type="button"
                    onClick={() => removeLine(index)}
                    disabled={fields.length <= 1}
                  >
                    <Trash2 className="h-4 w-4" />
                    {t("deliveryContent.deleteItem")}
                  </Button>
                </PopoverContent>
              </Popover>
            </div>
            {profileControlsShown && (
              <div className="flex w-full flex-row items-center gap-2">
                {fields.length > 1 && (
                  <Checkbox
                    className="ml-1 shrink-0"
                    checked={selectedFieldIds.has(field.id)}
                    onCheckedChange={(checked) => toggleLineSelection(field.id, checked === true)}
                    aria-label={t("deliveryContentBulk.selectLine")}
                  />
                )}
                <DeliveryContentLineProfileControls
                  index={index}
                  products={products}
                  isUsaProducts={isUsaProducts}
                  onChooseProfile={openPickerForLine}
                  onCreateProfile={openCreateForLine}
                  onEditProfile={openEditForLine}
                  destinationRegion={destinationRegion}
                  viaImportedInvoice={viaImportedInvoice}
                />
              </div>
            )}
          </div>
        ))}
        <div className="flex w-full flex-col items-center justify-between gap-2 @xl:flex-row">
          <Button
            className="w-fit"
            variant="outline"
            type="button"
            onClick={() => append(defaultValues)}
          >
            <Plus className="h-4 w-4" />
            {t("deliveryContent.addItem")}
          </Button>
          <DeliveryContentTotal />
        </div>
      </AccordionContent>
      <ProductProfilePickerDrawer
        open={pickerOpen}
        onClose={closePicker}
        products={products}
        onSelectProduct={assignProductToTargetLines}
        isUsaProducts={isUsaProducts}
        companyId={companyId}
        defaultColor={pickerDefaultColor}
      />
      <ProductDrawer
        open={drawerState.open}
        onClose={() => {
          setDrawerState({ open: false, product: null });
          profileLineIndicesRef.current = null;
        }}
        isUSA={isUsaProducts}
        product={drawerState.product}
        onProductSaved={assignProductToTargetLines}
        importerId={formImporterId}
        clientId={companyId}
      />
    </AccordionItem>
  );
};
